import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const Services = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Nos <span className="text-blue-600">Services</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des solutions complètes et professionnelles pour répondre à tous vos besoins fiscaux et comptables
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const isEven = index % 2 === 0;
              const colorClasses = isEven 
                ? { bg: 'bg-blue-100', text: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' }
                : { bg: 'bg-green-100', text: 'text-green-600', button: 'bg-green-600 hover:bg-green-700' };

              return (
                <div
                  key={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'lg:flex-row-reverse'
                  }`}
                >
                  <div className={isEven ? '' : 'lg:order-2'}>
                    <div className="w-44 h-36 overflow-hidden mb-6 shadow-md cursor-pointer transition-shadow duration-300 hover:shadow-[0_10px_40px_rgba(0,0,0,0.3)] border border-gray-300 bg-sky-50">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-contain"
                        data-testid={`service-image-${service.id}`}
                      />
                    </div>
                    <p className="text-lg text-gray-600 mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className={`w-6 h-6 ${colorClasses.text} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      className={`inline-flex items-center px-6 py-3 text-white ${colorClasses.button} rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-semibold`}
                    >
                      Demander un devis
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>

                  <div className={isEven ? '' : 'lg:order-1'}>
                    <div className={`bg-gradient-to-br ${
                      isEven ? 'from-blue-100 to-blue-200' : 'from-green-100 to-green-200'
                    } rounded-2xl p-8 shadow-xl`}>
                      <div className="bg-white rounded-xl p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">
                          Avantages clés
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full ${isEven ? 'bg-blue-600' : 'bg-green-600'} mt-2`}></div>
                            <div>
                              <p className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Times New Roman, serif' }}>Service rapide et efficace</p>
                              <p className="text-base text-gray-600" style={{ fontFamily: 'Arial, sans-serif' }}>Traitement dans les meilleurs délais</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full ${isEven ? 'bg-blue-600' : 'bg-green-600'} mt-2`}></div>
                            <div>
                              <p className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Times New Roman, serif' }}>Expertise professionnelle</p>
                              <p className="text-base text-gray-600" style={{ fontFamily: 'Arial, sans-serif' }}>Équipe qualifiée et expérimentée</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full ${isEven ? 'bg-blue-600' : 'bg-green-600'} mt-2`}></div>
                            <div>
                              <p className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Times New Roman, serif' }}>Tarifs transparents</p>
                              <p className="text-base text-gray-600" style={{ fontFamily: 'Arial, sans-serif' }}>Pas de frais cachés</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full ${isEven ? 'bg-blue-600' : 'bg-green-600'} mt-2`}></div>
                            <div>
                              <p className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Times New Roman, serif' }}>Support continu</p>
                              <p className="text-base text-gray-600" style={{ fontFamily: 'Arial, sans-serif' }}>Accompagnement tout au long de l'année</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Besoin d'aide pour votre fiscalité?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contactez-nous pour discuter de vos besoins et obtenir une soumission personnalisée
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Nous contacter
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/tarifs"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-200"
            >
              Voir les tarifs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
